import {
  type BillingProfile,
  type BillingProfileInput,
  COUNTRY_CODES,
  taxIdTypesFor,
} from '@noconflict/contracts';
import { z } from 'zod';
import { validationMessage } from '../../../shared/i18n/zod';

export const NO_TAX_ID = 'none';
export const MAX_BILLING_EMAILS = 5;

const text = (max: number) => z.string().trim().max(max, validationMessage('tooLong'));

/**
 * The form's own schema, with catalogue messages. It accepts exactly what `billingProfileInputSchema` accepts
 * (a test feeds both the same cases); `toInput` produces the contract shape.
 */
export const billingProfileFormSchema = z
  .object({
    legalName: text(120).min(1, validationMessage('legalNameRequired')),
    line1: text(120).min(1, validationMessage('line1Required')),
    line2: text(120),
    city: text(80).min(1, validationMessage('cityRequired')),
    region: text(80),
    postalCode: text(20).min(1, validationMessage('postalCodeRequired')),
    country: z.enum(COUNTRY_CODES, validationMessage('countryRequired')),
    taxIdType: z.string(),
    taxIdValue: text(40),
    billingEmails: z
      .array(
        z.object({
          value: z
            .string()
            .trim()
            .toLowerCase()
            .pipe(z.email(validationMessage('billingEmailInvalid')).max(254)),
        }),
      )
      .min(1)
      .max(MAX_BILLING_EMAILS),
    poRequired: z.boolean(),
  })
  .superRefine((v, ctx) => {
    const hasType = v.taxIdType !== NO_TAX_ID && taxIdTypesFor(v.country).includes(v.taxIdType);
    if (hasType && v.taxIdValue === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['taxIdValue'],
        message: validationMessage('taxIdValueRequired').error(),
      });
    }
    const seen = new Set<string>();
    v.billingEmails.forEach((e, i) => {
      if (seen.has(e.value)) {
        ctx.addIssue({
          code: 'custom',
          path: ['billingEmails', i, 'value'],
          message: validationMessage('billingEmailDuplicate').error(),
        });
      }
      seen.add(e.value);
    });
  });
export type BillingProfileFormInput = z.input<typeof billingProfileFormSchema>;
export type BillingProfileFormValues = z.output<typeof billingProfileFormSchema>;

/** Form values → the request body. A type that does not belong to the country is dropped with its value. */
export function toInput(v: BillingProfileFormValues): BillingProfileInput {
  const typed = v.taxIdType !== NO_TAX_ID && taxIdTypesFor(v.country).includes(v.taxIdType);
  return {
    legalName: v.legalName,
    line1: v.line1,
    line2: v.line2 || null,
    city: v.city,
    region: v.region || null,
    postalCode: v.postalCode,
    country: v.country,
    taxIdType: typed ? v.taxIdType : null,
    taxIdValue: typed ? v.taxIdValue : null,
    billingEmails: v.billingEmails.map((e) => e.value),
    poRequired: v.poRequired,
  };
}

/** A saved profile (or, for a new one, the org's country) → form defaults. */
export function toDefaults(
  profile: BillingProfile | null,
  orgCountry: string,
): BillingProfileFormInput {
  return {
    legalName: profile?.legal_name ?? '',
    line1: profile?.line1 ?? '',
    line2: profile?.line2 ?? '',
    city: profile?.city ?? '',
    region: profile?.region ?? '',
    postalCode: profile?.postal_code ?? '',
    country: (profile?.country ?? orgCountry) as BillingProfileFormInput['country'],
    taxIdType: profile?.tax_id_type ?? NO_TAX_ID,
    taxIdValue: profile?.tax_id_value ?? '',
    billingEmails: (profile?.billing_emails.length ? profile.billing_emails : ['']).map(
      (value) => ({
        value,
      }),
    ),
    poRequired: profile?.po_required ?? false,
  };
}
